import React, { useState, useMemo } from "react";
import { Link } from "wouter";
import {
  useListCreators,
  useListProducts,
  useListMembersContent,
} from "@workspace/api-client-react";
import {
  Sparkles,
  ShoppingBag,
  CreditCard,
  Wallet,
  Play,
  Image,
  Film,
  Star,
  Zap,
  BadgeCheck,
  ChevronRight,
  X,
  Lock,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { useWallet } from "@/hooks/useWallet";
import { useToast } from "@/hooks/use-toast";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { ContentOrientationToggle, type ContentOrientation } from "@/components/ContentOrientationToggle";
import { AdultContentToggle } from "@/components/AdultContentToggle";
import { AdultLockedLink, AdultLockOverlay } from "@/components/AdultLockedLink";

// Route slugs must match the routes defined in App.tsx — do NOT use creator.username here
const CREATOR_ROUTE_SLUG: Record<number, string> = {
  7: "illusionistz",
  8: "emeraldonyx",
  9: "mistyhaze",
  10: "claudiag",
  11: "avtheentertainer",
  12: "germani",
  13: "mst",
  14: "goldie",
  18: "rhythmandsoulpatrol",
  19: "joerussketchbook",
};

const CATEGORY_COLORS: Record<string, string> = {
  fashion: "from-purple-900/60 to-fuchsia-900/40 border-purple-700/40",
  art: "from-orange-900/60 to-amber-900/40 border-orange-700/40",
  music: "from-pink-900/60 to-rose-900/40 border-pink-700/40",
  fitness: "from-green-900/60 to-emerald-900/40 border-green-700/40",
  lifestyle: "from-teal-900/60 to-cyan-900/40 border-teal-700/40",
  gaming: "from-blue-900/60 to-indigo-900/40 border-blue-700/40",
  entertainment: "from-violet-900/60 to-purple-900/40 border-violet-700/40",
};

const CATEGORY_ACCENT: Record<string, string> = {
  fashion: "text-fuchsia-400",
  art: "text-orange-400",
  music: "text-pink-400",
  fitness: "text-green-400",
  lifestyle: "text-teal-400",
  gaming: "text-blue-400",
  entertainment: "text-violet-400",
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  digital: <Zap className="w-3 h-3" />,
  experience: <Star className="w-3 h-3" />,
  service: <BadgeCheck className="w-3 h-3" />,
  merch: <ShoppingBag className="w-3 h-3" />,
  physical: <ShoppingBag className="w-3 h-3" />,
};

const PRODUCT_TYPE_COLORS: Record<string, string> = {
  digital: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  experience: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  service: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  merch: "bg-rose-500/20 text-rose-300 border-rose-500/30",
  physical: "bg-rose-500/20 text-rose-300 border-rose-500/30",
};

const MEDIA_ICONS: Record<string, React.ReactNode> = {
  video: <Film className="w-3 h-3" />,
  photo: <Image className="w-3 h-3" />,
  series: <Play className="w-3 h-3" />,
  virtual: <Star className="w-3 h-3" />,
};

type CheckoutProduct = {
  id: number;
  name: string;
  price: number;
  priceUsd: number | null | undefined;
  currency: string;
  paymentOptions: string | undefined;
  imageUrl: string;
  sellerName: string;
  productType: string | undefined;
};

function CheckoutModal({
  product,
  open,
  onClose,
}: {
  product: CheckoutProduct | null;
  open: boolean;
  onClose: () => void;
}) {
  const { address, isConnected, connect } = useWallet();
  const { toast } = useToast();
  const { startCheckout, isLoading: stripeLoading } = useStripeCheckout();
  const [processing, setProcessing] = useState(false);

  if (!product) return null;

  const showBlockchain =
    !product.paymentOptions ||
    product.paymentOptions === "blockchain" ||
    product.paymentOptions === "both";
  const showCard =
    product.paymentOptions === "card" || product.paymentOptions === "both";

  const defaultTab = showCard ? "card" : "blockchain";
  const usdAmount = product.priceUsd ?? null;

  async function handleBlockchainPay() {
    if (!product) return;
    if (!isConnected) { await connect(); return; }
    setProcessing(true);
    await new Promise((r) => setTimeout(r, 1200));
    setProcessing(false);
    toast({
      title: "Transaction Submitted",
      description: `Paying ${product.price} ${product.currency} to ${product.sellerName} on Polygon Amoy.`,
    });
    onClose();
  }

  async function handleCardPay() {
    if (!product || !usdAmount) return;
    onClose();
    await startCheckout({
      productName: product.name,
      productType: product.productType ?? "digital",
      amountUsd: usdAmount,
      metadata: {
        productId: String(product.id),
        sellerName: product.sellerName,
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0e0e14] border border-white/10 max-w-md text-foreground">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">{product.name}</DialogTitle>
        </DialogHeader>

        {/* Product summary */}
        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 mb-2">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-14 h-14 rounded-lg object-cover"
          />
          <div>
            <p className="text-xs text-muted-foreground">{product.sellerName}</p>
            <p className="font-semibold text-sm">{product.name}</p>
            <div className="flex items-center gap-2 mt-1">
              {showBlockchain && (
                <span className="text-xs font-mono text-violet-300">
                  {product.price} {product.currency}
                </span>
              )}
              {usdAmount != null && (
                <span className="text-xs text-muted-foreground">
                  ≈ ${usdAmount.toFixed(2)} USD
                </span>
              )}
            </div>
          </div>
        </div>

        <Tabs defaultValue={defaultTab}>
          <TabsList className="w-full bg-white/5 border border-white/10">
            {showCard && (
              <TabsTrigger value="card" className="flex-1 gap-1.5 text-xs">
                <CreditCard className="w-3 h-3" /> Pay with Card
              </TabsTrigger>
            )}
            {showBlockchain && (
              <TabsTrigger value="blockchain" className="flex-1 gap-1.5 text-xs">
                <Wallet className="w-3 h-3" /> Pay with GLO / HALO
              </TabsTrigger>
            )}
          </TabsList>

          {/* ── Card tab: redirect to Stripe Checkout ── */}
          {showCard && (
            <TabsContent value="card" className="mt-4 space-y-4">
              {usdAmount != null ? (
                <>
                  <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-xs">Item</span>
                      <span className="text-xs font-semibold">{product.name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground text-xs">Seller</span>
                      <span className="text-xs">{product.sellerName}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2">
                      <span className="text-muted-foreground text-xs font-mono uppercase tracking-widest">Total</span>
                      <span className="text-base font-bold font-mono text-sky-300">
                        ${usdAmount.toFixed(2)} USD
                      </span>
                    </div>
                  </div>

                  <Button
                    className="w-full gap-2 bg-sky-600 hover:bg-sky-500 font-semibold"
                    onClick={handleCardPay}
                    disabled={stripeLoading}
                  >
                    <CreditCard className="w-4 h-4" />
                    {stripeLoading ? "Redirecting…" : `Pay $${usdAmount.toFixed(2)} — Secure Checkout`}
                  </Button>
                  <p className="text-[10px] text-muted-foreground text-center">
                    You'll be redirected to Stripe's secure checkout page · No card data stored here
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  USD price not available for this item. Use crypto payment below.
                </p>
              )}
            </TabsContent>
          )}

          {/* ── Blockchain tab: wallet payment ── */}
          {showBlockchain && (
            <TabsContent value="blockchain" className="mt-4 space-y-3">
              <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground text-xs">Network</span>
                  <span className="text-xs font-mono text-violet-300">Polygon Amoy</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground text-xs">Token</span>
                  <span className="text-xs font-mono">{product.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-xs">Amount</span>
                  <span className="text-sm font-bold font-mono text-violet-300">
                    {product.price} {product.currency}
                  </span>
                </div>
              </div>

              {isConnected && address ? (
                <div className="text-xs text-green-400 font-mono bg-green-500/10 border border-green-500/20 rounded px-3 py-2">
                  Connected: {address.slice(0, 10)}…{address.slice(-8)}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Connect your wallet to pay with GLO or HALO tokens on Polygon Amoy.
                </p>
              )}

              <Button
                className="w-full gap-2 bg-violet-600 hover:bg-violet-500 font-semibold"
                onClick={handleBlockchainPay}
                disabled={processing}
              >
                <Wallet className="w-4 h-4" />
                {processing
                  ? "Processing…"
                  : isConnected
                  ? `Pay ${product.price} ${product.currency}`
                  : "Connect Wallet to Pay"}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center">
                Transaction recorded on Polygon Amoy testnet
              </p>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default function CreatorsHub() {
  const [contentOrientation, setContentOrientation] = useState<ContentOrientation>("day");
  const [showAdultContent, setShowAdultContent] = useState(false);
  const { data: creators, isLoading: creatorsLoading } = useListCreators();
  const { data: allProducts, isLoading: productsLoading } = useListProducts({});
  const { data: featuredContent } = useListMembersContent({ orientation: contentOrientation, showAdult: showAdultContent });

  async function changeContentOrientation(next: ContentOrientation) {
    setContentOrientation(next);
  }

  const [selectedCreator, setSelectedCreator] = useState<number | "all">("all");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [checkoutProduct, setCheckoutProduct] = useState<CheckoutProduct | null>(null);

  const creatorsById = useMemo(
    () => Object.fromEntries((creators ?? []).map((c) => [c.id, c])),
    [creators]
  );

  const studioProducts = (allProducts ?? []).filter((p) => p.creatorId != null);
  const filtered = studioProducts.filter((p) => {
    const creatorMatch = selectedCreator === "all" || p.creatorId === selectedCreator;
    const typeMatch = selectedType === "all" || p.productType === selectedType;
    return creatorMatch && typeMatch;
  });

  const productTypes = ["all", ...Array.from(new Set(studioProducts.map((p) => p.productType ?? "digital")))];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950/40 via-[#0a0a10] to-fuchsia-950/20" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_40%_at_50%_-20%,rgba(139,92,246,0.15),transparent)]" />
        <div className="relative container mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-xs font-mono mb-6">
            <Sparkles className="w-3 h-3" />
            CREATORS / STUDIOS
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            The Creator<br />
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
              Economy Hub
            </span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Support your favorite creators directly. Buy their content, services, and products
            using GLO/HALO tokens on Polygon Amoy — or pay with a traditional credit card.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600/20 border border-violet-500/30 text-sm">
              <Wallet className="w-4 h-4 text-violet-400" />
              <span className="text-violet-300 font-medium">Blockchain · GLO / HALO</span>
              <span className="text-xs text-muted-foreground">Polygon Amoy</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-sky-600/20 border border-sky-500/30 text-sm">
              <CreditCard className="w-4 h-4 text-sky-400" />
              <span className="text-sky-300 font-medium">Traditional · Card / Bank</span>
            </div>
          </div>
        </div>
      </section>

      {/* Creators Grid */}
      <section className="py-16 px-4 border-b border-white/5">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">Featured Creators</h2>
            <Badge variant="outline" className="text-muted-foreground font-mono text-xs">
              {creators?.length ?? 0} studios
            </Badge>
          </div>

          {creatorsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-52 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {(creators ?? []).map((creator, i) => {
                const cat = creator.category ?? "entertainment";
                const colors = CATEGORY_COLORS[cat] ?? CATEGORY_COLORS.entertainment;
                const accent = CATEGORY_ACCENT[cat] ?? "text-violet-400";
                const profileRoute: Record<number, string> = {
                  7: "/creators/illusionistz",
                  8: "/creators/emeraldonyx",
                  9: "/creators/mistyhaze",
                  10: "/creators/claudiag",
                  11: "/creators/avtheentertainer",
                  12: "/creators/germani",
                  13: "/creators/mst",
                  14: "/creators/goldie",
                  18: "/creators/rhythmandsoulpatrol",
                  19: "/creators/joerussketchbook",
                };
                const profilePath = profileRoute[creator.id];
                return (
                  <motion.div
                    key={creator.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`relative rounded-xl border bg-gradient-to-b ${colors} p-4 flex flex-col gap-3 group`}
                  >
                    {creator.verified && (
                      <BadgeCheck className="absolute top-3 right-3 w-4 h-4 text-blue-400" />
                    )}
                    <img
                      src={creator.avatarUrl ?? ""}
                      alt={creator.displayName}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-white/10"
                    />
                    <div>
                      <p className="font-bold text-sm text-white leading-tight">{creator.displayName}</p>
                      <p className="text-xs text-muted-foreground">@{creator.username}</p>
                      <p className={`text-xs font-mono uppercase mt-1 ${accent}`}>{creator.category}</p>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <p><strong className="text-foreground">{(creator.subscriberCount ?? 0).toLocaleString()}</strong> subscribers</p>
                      {creator.subscriptionPriceHalo && (
                        <p><strong className="text-foreground">{Number(creator.subscriptionPriceHalo).toFixed(2)} HALO</strong> / month</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 mt-auto">
                      {profilePath ? (
                        <Link href={profilePath}>
                          <Button size="sm" className="w-full text-xs gap-1 bg-white/10 hover:bg-white/20 text-white border-0 h-7">
                            View Profile <ChevronRight className="w-3 h-3" />
                          </Button>
                        </Link>
                      ) : (
                        <Button size="sm" disabled className="w-full text-xs gap-1 h-7 opacity-50 cursor-not-allowed">
                          <Lock className="w-3 h-3" /> Profile Soon
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full text-xs gap-1 h-7 border-white/10 hover:bg-white/10"
                        onClick={() => {
                          setSelectedCreator(creator.id);
                          document.getElementById("studio-store")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        <ShoppingBag className="w-3 h-3" /> Browse Studio
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Featured Content */}
      {featuredContent && (
        <section className="py-16 px-4 border-b border-white/5">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
              <h2 className="text-2xl font-bold text-white">Latest Creator Content</h2>
              <div className="flex items-center gap-3">
                <ContentOrientationToggle value={contentOrientation} onChange={changeContentOrientation} />
                <AdultContentToggle value={showAdultContent} onChange={setShowAdultContent} />
                <Link href="/content">
                  <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1">
                    See all <ChevronRight className="w-3 h-3" />
                  </Button>
                </Link>
              </div>
            </div>
            {featuredContent.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {featuredContent.slice(0, 8).map((item) => {
                const routeSlug = CREATOR_ROUTE_SLUG[item.creatorId ?? 0];
                const postHref = routeSlug ? `/creators/${routeSlug}/post/${item.id}` : undefined;
                const CardWrapper = postHref
                  ? ({ children }: { children: React.ReactNode }) => (
                      <AdultLockedLink item={item} href={postHref} className="block group relative rounded-xl overflow-hidden bg-white/5 border border-white/10 aspect-[4/3] cursor-pointer hover:ring-2 hover:ring-violet-500/50 transition-all">
                        {children}
                      </AdultLockedLink>
                    )
                  : ({ children }: { children: React.ReactNode }) => (
                      <div className="group relative rounded-xl overflow-hidden bg-white/5 border border-white/10 aspect-[4/3] cursor-pointer">
                        {children}
                      </div>
                    );
                return (
                  <CardWrapper key={item.id}>
                  <img
                    src={item.thumbnailUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {item.isAdult && <AdultLockOverlay />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                      item.tier === "premium"
                        ? "bg-amber-500/80 text-amber-100"
                        : item.tier === "standard"
                        ? "bg-sky-600/80 text-sky-100"
                        : "bg-green-600/80 text-green-100"
                    }`}>
                      {item.tier?.toUpperCase()}
                    </span>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-muted-foreground">
                      {MEDIA_ICONS[item.mediaType ?? "photo"]}
                    </span>
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-xs font-medium text-white truncate">{item.title}</p>
                    <p className="text-[10px] text-muted-foreground">{item.gasFeeGlo} GLO</p>
                  </div>
                  </CardWrapper>
                );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-white/10 bg-white/[0.03] py-12 text-center text-sm text-muted-foreground">
                No {contentOrientation.toUpperCase()} creator content is available yet.
              </div>
            )}
          </div>
        </section>
      )}

      {/* Studio Store */}
      <section id="studio-store" className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Studio Store</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Products and services from creators — pay on-chain or by card
              </p>
            </div>
            <Badge variant="outline" className="text-muted-foreground font-mono text-xs">
              {filtered.length} items
            </Badge>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCreator("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedCreator === "all"
                    ? "bg-violet-600 border-violet-500 text-white"
                    : "border-white/10 text-muted-foreground hover:text-white hover:border-white/20"
                }`}
              >
                All Creators
              </button>
              {(creators ?? [])
                .filter((c) => studioProducts.some((p) => p.creatorId === c.id))
                .map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCreator(c.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedCreator === c.id
                        ? "bg-violet-600 border-violet-500 text-white"
                        : "border-white/10 text-muted-foreground hover:text-white hover:border-white/20"
                    }`}
                  >
                    <img src={c.avatarUrl ?? ""} alt="" className="w-4 h-4 rounded-full object-cover" />
                    {c.displayName}
                  </button>
                ))}
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {productTypes.map((t) => (
                <button
                  key={t}
                  onClick={() => setSelectedType(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors capitalize ${
                    selectedType === t
                      ? "bg-sky-600 border-sky-500 text-white"
                      : "border-white/10 text-muted-foreground hover:text-white hover:border-white/20"
                  }`}
                >
                  {t === "all" ? "All Types" : t}
                </button>
              ))}
            </div>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-80 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>No items match your filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((product, i) => {
                const creator = (creators ?? []).find((c) => c.id === product.creatorId);
                const typeColor = PRODUCT_TYPE_COLORS[product.productType ?? "digital"] ?? PRODUCT_TYPE_COLORS.digital;
                const showBlockchain =
                  !product.paymentOptions ||
                  product.paymentOptions === "blockchain" ||
                  product.paymentOptions === "both";
                const showCard =
                  product.paymentOptions === "card" || product.paymentOptions === "both";

                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="rounded-xl border border-white/10 bg-card overflow-hidden flex flex-col group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium border ${typeColor}`}>
                          {TYPE_ICONS[product.productType ?? "digital"]}
                          {(product.productType ?? "digital").toUpperCase()}
                        </span>
                      </div>
                      {product.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-amber-500/80 text-amber-100">
                            <Star className="w-2.5 h-2.5" /> FEATURED
                          </span>
                        </div>
                      )}
                      {/* Payment badges */}
                      <div className="absolute bottom-3 left-3 flex gap-1.5">
                        {showBlockchain && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-600/80 text-[10px] text-white font-mono">
                            <Wallet className="w-2.5 h-2.5" /> {product.currency}
                          </span>
                        )}
                        {showCard && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-600/80 text-[10px] text-white">
                            <CreditCard className="w-2.5 h-2.5" /> CARD
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="p-4 flex flex-col gap-3 flex-1">
                      {creator && (
                        <div className="flex items-center gap-2">
                          <img
                            src={creator.avatarUrl ?? ""}
                            alt={creator.displayName}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                          <span className="text-xs text-muted-foreground">{creator.displayName}</span>
                          {creator.verified && <BadgeCheck className="w-3 h-3 text-blue-400 ml-auto" />}
                        </div>
                      )}

                      <div>
                        <h3 className="font-semibold text-sm text-white leading-tight">{product.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                      </div>

                      <div className="flex items-baseline gap-2 mt-auto">
                        <span className="text-base font-bold font-mono">
                          {product.price} {product.currency}
                        </span>
                        {product.priceUsd != null && (
                          <span className="text-xs text-muted-foreground">≈ ${product.priceUsd.toFixed(2)}</span>
                        )}
                      </div>

                      {/* Shop link — update href per item when individual URLs are ready */}
                      <a
                        href="https://ismprodco.com/shop"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md bg-primary text-primary-foreground text-xs font-semibold tracking-wide hover:bg-primary/90 transition-colors"
                      >
                        <ShoppingBag className="w-3 h-3" />
                        Shop at ISMprodco.com
                        <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                      </a>

                      <div className="flex gap-2">
                        {showBlockchain && (
                          <Button
                            size="sm"
                            className="flex-1 gap-1.5 text-xs bg-violet-600 hover:bg-violet-500 h-8"
                            onClick={() =>
                              setCheckoutProduct({
                                id: product.id,
                                name: product.name,
                                price: product.price,
                                priceUsd: product.priceUsd,
                                currency: product.currency,
                                paymentOptions: "blockchain",
                                imageUrl: product.imageUrl,
                                sellerName: product.sellerName,
                                productType: product.productType,
                              })
                            }
                          >
                            <Wallet className="w-3 h-3" /> GLO / HALO
                          </Button>
                        )}
                        {showCard && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 gap-1.5 text-xs border-sky-500/40 text-sky-300 hover:bg-sky-500/10 h-8"
                            onClick={() =>
                              setCheckoutProduct({
                                id: product.id,
                                name: product.name,
                                price: product.price,
                                priceUsd: product.priceUsd,
                                currency: product.currency,
                                paymentOptions: "card",
                                imageUrl: product.imageUrl,
                                sellerName: product.sellerName,
                                productType: product.productType,
                              })
                            }
                          >
                            <CreditCard className="w-3 h-3" /> Card
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <CheckoutModal
        product={checkoutProduct}
        open={!!checkoutProduct}
        onClose={() => setCheckoutProduct(null)}
      />
    </div>
  );
}
